import { PlusCircleIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import type { IconNames } from "components/Icon/Icon";
import Button from "common/components/Button/Button";
import Icon from "components/Icon/Icon";
import clsx from "clsx";
import CrudPagination from "./CrudPagination";
import CrudFilters, { ICrudFiltersProps } from "./CrudFilters";
import CrudTabs, { type CrudTab } from "./CrudTabs";

export type DefaultHeaderAction = "openSidebar" | "openModal" | "openPage";

export type CrudHeaderAction = {
  title: string;
  icon?: IconNames;
  action?: DefaultHeaderAction | (() => void);
  link?: string;
  disabled?: boolean;
  type?: string;
  className?: string;
  compact?: boolean;
  render?: () => React.ReactNode;
};

export type CrudHeaderProps = {
  title: string;
  subtitle?: string;
  icon?: IconNames;
  headerActions?: CrudHeaderAction[];
  compact?: boolean;
  colorMode?: "light" | "dark";
  pageCount: number;
  itemsPerPage?: number;
  itemsCount?: number;
  setPageIndex: (pageIndex: number) => void;
  pageIndex: number;
  isFetching?: boolean;
  tabs?: CrudTab[];
  activeTab?: string | null;
  onTabChange?: (value: string | null) => void;
} & ICrudFiltersProps;

function CrudHeader({
  title,
  subtitle,
  icon,
  headerActions = [],
  compact = false,
  colorMode = "dark",
  pageIndex,
  setPageIndex,
  pageCount: controlledPageCount,
  itemsPerPage = 0,
  itemsCount = 0,
  isFetching = false,
  tabs,
  activeTab,
  onTabChange,
  ...filtersProps
}: CrudHeaderProps) {
  const isLightMode = colorMode === "light";
  const titleClass = isLightMode ? "text-white" : "text-primary";
  const subtitleClass = isLightMode ? "text-white/90" : "text-primary-500";
  const iconWrapperClass = isLightMode
    ? "bg-primary-50/20 border-primary-200"
    : "bg-transparent border-primary-700";
  const iconClass = isLightMode ? "text-white" : "text-primary-700";
  const addButtonClass = isLightMode
    ? "bg-primary-50 text-primary"
    : "bg-primary-50 hover:bg-primary-100 text-primary border border-primary-200";
  const addButtonIconClass = isLightMode ? "text-primary" : "text-white";
  const addActionClassName = isLightMode
    ? "text-primary bg-primary-50"
    : "bg-gradient-to-r from-secondary-300 to-secondary text-white hover:opacity-90";

  return (
    <div className="flex flex-col justify-center relative lg:min-h-[70px] gap-4">
      <div
        className={clsx(
          "flex flex-col lg:flex-row justify-between lg:items-center w-full",
          !compact && "pt-4",
          !tabs && "mb-4"
        )}
      >
        <div className="flex-1">
          <div className="flex flex-row gap-4">
            <div
              className={`flex items-center font-bold ${titleClass} ${
                compact ? "text-md gap-2" : "text-2xl gap-4"
              }`}
            >
              {!compact && icon && (
                <div
                  className={`p-1 rounded-lg border-[1px] ${iconWrapperClass}`}
                >
                  <Icon name={icon} className={`w-7 h-7 ${iconClass}`} />
                </div>
              )}
              {compact && icon && (
                <Icon name={icon} className={`w-5 h-5 ${iconClass}`} />
              )}
              <div>
                <h1
                  className={`text-black ${compact ? "text-lg" : "text-3xl"}`}
                >
                  {title}
                </h1>
                {!compact && subtitle && (
                  <div className={`text-m font-light mt-2 ${subtitleClass}`}>
                    {subtitle}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3 items-center">
          {controlledPageCount > 1 && (
            <CrudPagination
              setPageIndex={setPageIndex}
              totalPage={controlledPageCount}
              disabled={isFetching}
              pageIndex={pageIndex}
              itemsPerPage={itemsPerPage}
              itemsCount={itemsCount}
              colorMode={colorMode}
            />
          )}

          {/* Add buttons */}

          <div className="flex flex-col lg:flex-row gap-4">
            {headerActions.map(
              (
                {
                  title: headerActionTitle,
                  icon: headerActionIcon,
                  action: headerAction,
                  link: headerActionLink,
                  disabled: headerActionDisabled,
                  compact: headerActionCompact,
                  type: headerActionType,
                  className: headerActionClassName,
                  render: headerActionRender,
                },
                index
              ) => {
                if (headerActionRender) {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`header-action-custom-${index}`}>
                      {headerActionRender()}
                    </div>
                  );
                }
                return headerActionLink ? (
                  <Link
                    to={headerActionLink}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`header-action-link-${index}`}
                    className={`${
                      headerActionDisabled ? "pointer-events-none" : ""
                    } flex justify-end self-start mt-3 md:mt-0 mb-4 lg:mb-0 ${headerActionClassName}`}
                  >
                    <div
                      className={`flex cursor-pointer px-3 py-2 gap-3 rounded-md ${addButtonClass}`}
                    >
                      {!headerActionCompact && (
                        <div
                          className={`flex flex-col justify-center text-left ${
                            headerActionDisabled && "opacity-40"
                          }`}
                        >
                          {headerActionTitle && (
                            <span className="font-bold text-right">
                              {headerActionTitle}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`flex flex-col items-center justify-center text-left ${
                          headerActionDisabled && "opacity-40"
                        }`}
                      >
                        <PlusCircleIcon
                          className={`w-5 h-5 ${addButtonIconClass}`}
                        />
                      </div>
                    </div>
                  </Link>
                ) : (
                  <Button
                    title={headerActionTitle}
                    icon={headerActionIcon}
                    onClick={headerAction as () => void}
                    disabled={headerActionDisabled}
                    type={headerActionType}
                    className={`${addActionClassName} ${headerActionClassName}`}
                    compact={headerActionCompact}
                  />
                );
              }
            )}
          </div>
        </div>
      </div>
      {tabs && tabs.length > 0 && onTabChange && (
        <CrudTabs
          tabs={tabs}
          activeValue={activeTab ?? null}
          onChange={onTabChange}
        />
      )}
      <CrudFilters {...filtersProps} />
    </div>
  );
}

export default CrudHeader;
